import { strongTextFormatter } from "@components/fileUploader/File.utils";
import { DateRangeSpecialValue } from "@components/inputs/date/popup/Calendar.utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import {
    enhanceFieldVisibility,
    ifAny,
    IFieldDef,
    IFieldInfoProperties,
    IGetValueArgs,
    isVisibleByPath,
    not,
    TGetValueFn
} from "@components/smart/FieldInfo";
import {
    BirthNumberFieldDef,
    CZPostalCodePlaceholder,
    EmailDef,
    getPostalCodeDef,
    PhoneNumberDef,
    SymbolSpecificDef
} from "@components/smart/GeneralFieldDefinition";
import SmartFastEntryList, { addNewLineItem, ISmartFastEntryListProps } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { formAttachmentFieldDef } from "@components/smart/smartFileInput/SmartFileInput.utils";
import { IFormGroupDef, IGroupRenderFn } from "@components/smart/smartFormGroup/SmartFormGroup";
import { smartStrongTextFormatter } from "@components/smart/smartTable/SmartTable.utils";
import BindingContext, { createPath } from "@odata/BindingContext";
import { getBoundValue } from "@odata/Data.utils";
import {
    CompanyEntity,
    EntitySetName,
    EntityTypeName,
    GovernmentInstitutionEntity,
    IPrCSSZSubmissionCaregivingEntity,
    IPrCSSZSubmissionIllnessLeaveEntity,
    IPrCSSZSubmissionLongtermCaregivingEntity,
    IPrCSSZSubmissionMaternityLeaveEntity,
    IPrCSSZSubmissionPaternityLeaveEntity,
    IPrSocialEventEntity,
    PrCSSZDecisivePeriodEntity,
    PrCSSZPaymentConnectionDeliveryEntity,
    PrCSSZPaymentConnectionWireTransferAbroadEntity,
    PrCSSZPaymentConnectionWireTransferCZEntity,
    PrCSSZPaymentMethodEntity,
    PrCSSZSubmissionCaregivingDetailEntity,
    PrCSSZSubmissionCaregivingEntity,
    PrCSSZSubmissionCompanyEntity,
    PrCSSZSubmissionDetailEntity,
    PrCSSZSubmissionEmployeeEntity,
    PrCSSZSubmissionEmployerConfirmationCaregivingEntity,
    PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity,
    PrCSSZSubmissionEmploymentEntity,
    PrCSSZSubmissionIllnessLeaveDetailEntity,
    PrCSSZSubmissionIllnessLeaveEntity,
    PrCSSZSubmissionLongtermCaregivingDetailEntity,
    PrCSSZSubmissionLongtermCaregivingEntity,
    PrCSSZSubmissionMaternityLeaveDetailEntity,
    PrCSSZSubmissionMaternityLeaveEntity,
    PrCSSZSubmissionPaternityLeaveDetailEntity,
    PrCSSZSubmissionPaternityLeaveEntity,
    PrCSSZSubmissionPaymentBasisCaregivingEntity,
    PrCSSZSubmissionPaymentBasisEntity,
    PrCSSZSubmissionPaymentBasisPaternityLeaveEntity,
    PrEmployeeBankAccountEntity,
    PrEmployeeEntity,
    PrEmploymentEntity,
    PrPensionTypeEntity,
    PrSocialEventEntity,
    PrSubmissionCaregivingIntervalEntity,
    PrSubmissionEntity,
    PrSubsequentMaternityLeaveStatusEntity,
    PrWorkedInDaysIntervalEntity
} from "@odata/GeneratedEntityTypes";
import {
    CountryCode,
    GovernmentInstitutionTypeCode,
    PrCSSZPaymentMethodCode,
    PrPensionTypeCode,
    PrSubsequentMaternityLeaveStatusCode
} from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { getBankAccountsFieldBaseDef } from "@pages/banks/bankAccounts/BankAccounts.utils";
import { getGovernmentInstitutionFilterDefByType } from "@pages/companies/Company.utils";
import { IDefinition } from "@pages/PageUtils";
import {
    employeeWithDependentEmploymentSelectDef,
    getEmploymentSelectDef,
    getIdentificationTypeSelectDef,
    IdentificationType,
    IdentificationTypeLocalPath,
    isOfIdentType
} from "@pages/payroll/employee/Employee.utils";
import { getEmploymentTypeDef } from "@pages/payroll/employment/Employment.utils";
import {
    getPayrollCurrencyFieldDef,
    getPayrollCurrencyFormatter,
    getPayrollCurrencyUnit
} from "@pages/payroll/payroll.utils";
import DecisivePeriodsSmartFastEntryList, { SummaryRowId } from "@pages/payroll/submission/components/DecisivePeriodsSmartFastEntryList";
import { CareGivingLabelStyled, LabelStyled } from "@pages/payroll/submission/Submission.styles";
import { isNewEntity } from "@utils/definition.utils";
import { forEachKey, isDefined, isObjectEmpty } from "@utils/general";
import { capitalize } from "@utils/string";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import React from "react";

import { DASH_CHARACTER } from "../../../../constants";
import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import {
    BasicInputSizes,
    EditableTextSizes,
    FastEntryInputSizes,
    FieldType,
    GroupedField,
    LabelStatus,
    NavigationSource
} from "../../../../enums";
import { TValue } from "../../../../global.types";
import DateType, { DateFormat, getUtcDayjs } from "../../../../types/Date";
import { addFormDef, addFormGroup, getFormGroup } from "../../../../views/formView/Form.utils";
import { FormStorage } from "../../../../views/formView/FormStorage";
import { IGetSubmissionDefArgs, PrSubmissionGroup } from "../Submission.def";

export const IncomeTypeLocalPath = BindingContext.localContext("IncomeType");
export const WorkedInDaysLocalPath = BindingContext.localContext("WorkedInDays");

export type IPrCSSZSubmissionEntity = IPrCSSZSubmissionMaternityLeaveEntity | IPrCSSZSubmissionPaternityLeaveEntity;
type PrCSSZSubmissionEntityKeys = Extract<Extract<Extract<Extract<
    keyof typeof PrCSSZSubmissionMaternityLeaveEntity,
    keyof typeof PrCSSZSubmissionPaternityLeaveEntity>,
    keyof typeof PrCSSZSubmissionCaregivingEntity>,
    keyof typeof PrCSSZSubmissionLongtermCaregivingEntity>,
    keyof typeof PrCSSZSubmissionIllnessLeaveEntity>;

export const PrCSSZSubmissionEntity: Partial<Record<PrCSSZSubmissionEntityKeys, string>> = {
    Id: PrCSSZSubmissionMaternityLeaveEntity.Id,
    Detail: PrCSSZSubmissionMaternityLeaveEntity.Detail,
    Employee: PrCSSZSubmissionMaternityLeaveEntity.Employee,
};

export enum CSSZSubmissionIncomeType {
    DecisivePeriod = "DecisivePeriod",
    EstimatedIncomeAmount = "EstimatedIncomeAmount"
}

// Translations specific for CSSZ submissions
export const CSSZTranslationFiles = [
    "SocialEvents", "Employee",
    getEnumNameSpaceName(EntityTypeName.GovernmentInstitution)
];

export function getIconNameFromEntitySet(entitySet: EntitySetName): string {
    switch (entitySet) {
        case EntitySetName.PrCSSZSubmissionsPaternityLeave:
        case EntitySetName.PrCSSZSubmissionsMaternityLeave:
        case EntitySetName.PrCSSZSubmissionsCaregiving:
        case EntitySetName.PrCSSZSubmissionsLongtermCaregiving:
        case EntitySetName.PrCSSZSubmissionsIllnessLeave:
            return "NEMPRI";
        default:
            return null;
    }
}

export function addWorkedInDaysIntervalLineItem(storage: FormStorage, collectionBc: BindingContext): void {
    const columns: IFieldDef[] = [
        // WorkScheduleDays or WorkLeaveDays or WorkedInDays
        { id: PrWorkedInDaysIntervalEntity.DateFrom }, { id: PrWorkedInDaysIntervalEntity.DateTo }
    ];
    const groupId = storage._getGroupKeyFromBc(collectionBc.navigate(columns[0].id));
    addNewLineItem(storage, collectionBc.getNavigationPath(), groupId, columns);
}

export function getSocialSecurityFieldDef(): IFieldInfoProperties {
    return {
        type: FieldType.ComboBox,
        ...getGovernmentInstitutionFilterDefByType(GovernmentInstitutionTypeCode.CzechSocialSecurityAdministration),
        fieldSettings: {
            displayName: GovernmentInstitutionEntity.ShortName,
            shouldDisplayAdditionalColumns: true,
            localDependentFields: [
                {
                    from: { id: GovernmentInstitutionEntity.OpenDataCode },
                    to: { id: GovernmentInstitutionEntity.OpenDataCode },
                    navigateFrom: NavigationSource.Itself
                }
            ]
        },
        columns: [
            { id: GovernmentInstitutionEntity.ShortName },
            { id: GovernmentInstitutionEntity.OpenDataCode }
        ],
        additionalProperties: [
            { id: GovernmentInstitutionEntity.OpenDataCode }
        ]
    };
}

export function initWorkedInDays(storage: FormStorage): void {
    const workedInDaysBc = storage.data.bindingContext
        .navigate(PrCSSZSubmissionCaregivingEntity.Detail)
        .navigate(PrCSSZSubmissionCaregivingDetailEntity.PaymentBasis)
        .navigate(PrCSSZSubmissionPaymentBasisCaregivingEntity.WorkedInDays);
    const workedInDays = storage.getValue(workedInDaysBc);
    const hasWorkedInDays = workedInDays?.length > 0;
    storage.setValueByPath(WorkedInDaysLocalPath, hasWorkedInDays);
}

export function handleWorkedInDaysChange(e: ISmartFieldChange, storage: FormStorage): void {
    const path = e.bindingContext.getPath();
    if (path === WorkedInDaysLocalPath) {
        const workedInDaysBc = storage.data.bindingContext
            .navigate(PrCSSZSubmissionCaregivingEntity.Detail)
            .navigate(PrCSSZSubmissionCaregivingDetailEntity.PaymentBasis)
            .navigate(PrCSSZSubmissionPaymentBasisCaregivingEntity.WorkedInDays);
        if (e.value) {
            // create first empty line item
            addWorkedInDaysIntervalLineItem(storage, workedInDaysBc);
        } else {
            // remove all line items
            storage.setValue(workedInDaysBc, []);
        }
        // refresh whole page to re-render group and show fast entry
        storage.refresh(true);
    }
}

export function getConditionalFastEntryListRenderer(visibilityPath: string) {
    return (args: IGroupRenderFn): React.ReactElement => {
        const { storage } = args;
        const isVisible = storage.getValueByPath(visibilityPath);
        if (!isVisible) {
            return null;
        }
        return (<>
            <SmartFastEntryList {...(args.props as unknown as ISmartFastEntryListProps)} />
        </>);
    };
}

const workedOnEventStartDate: TGetValueFn<boolean> = ({ storage }) => {
    return !!storage.getValueByPath(createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.HasWorkedOnEventStartDate));
};

const isIncomeType = (type: CSSZSubmissionIncomeType): TGetValueFn<boolean> => {
    return ({ storage }) => {
        const incomeType = storage.getValueByPath(IncomeTypeLocalPath);
        return incomeType === type;
    };
};

const isPaymentType = (type: PrCSSZPaymentMethodCode): TGetValueFn<boolean> => {
    return ({ storage }) => {
        const paymentType = storage.getValueByPath(createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.PaymentMethod))?.Code;
        return paymentType === type;
    };
};

const isSummaryRow: TGetValueFn<boolean> = (args) => {
    const { bindingContext } = args;
    return bindingContext.getParent().getKey() === SummaryRowId;
};

function preparePaymentMethodAddressFieldDefs(d: IDefinition, prefix: string, method: PrCSSZPaymentMethodCode) {
    addFormDef(d, createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.Street), {
        width: BasicInputSizes.XL,
        groupedField: GroupedField.MultiStart,
        isVisible: isPaymentType(method)
    });
    addFormDef(d, createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.HouseNumber), {
        width: BasicInputSizes.XS,
        label: i18next.t("Submission:SocialEventsForm.HouseNumber"),
        isVisible: isPaymentType(method)
    });
    addFormDef(d, createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.StreetNumber), {
        width: BasicInputSizes.XS,
        groupedField: GroupedField.MultiEnd,
        label: i18next.t("Submission:SocialEventsForm.StreetNumber"),
        isVisible: isPaymentType(method)
    });
    addFormDef(d, createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.City), {
        groupedField: GroupedField.MultiStart,
        isVisible: isPaymentType(method)
    });
    const isCZAddress = method === PrCSSZPaymentMethodCode.PaymentToAddress;
    const commonPostalCodeDef = cloneDeep(getPostalCodeDef(isCZAddress));
    commonPostalCodeDef.fieldSettings.placeholder = CZPostalCodePlaceholder;
    addFormDef(d, createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.PostalCode), {
        ...commonPostalCodeDef,
        groupedField: GroupedField.MultiEnd,
        isVisible: isPaymentType(method)
    });
    return [
        { id: createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.Street) },
        { id: createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.HouseNumber) },
        { id: createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.StreetNumber) },
        { id: createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.City) },
        { id: createPath(prefix, PrCSSZPaymentConnectionDeliveryEntity.PostalCode) }
    ];
}

export function addCSSZSwitchDef(d: IDefinition, path: string, labelKey?: string, dependentPathsOrDef?: string[] | IFieldInfoProperties, def: IFieldInfoProperties = {}): void {
    const label = isDefined(labelKey) ? i18next.t(`Submission:SocialEventsForm.${labelKey}`) : undefined;
    const dependentPaths: string[] = [];
    if (Array.isArray(dependentPathsOrDef)) {
        dependentPaths.push(...dependentPathsOrDef);
    } else {
        Object.assign(def, dependentPathsOrDef);
    }
    addFormDef(d, path, {
        type: FieldType.Switch,
        ...(label ? { label } : {}),
        defaultValue: false,
        fieldSettings: {
            type: SwitchType.YesNo
        },
        affectedFields: dependentPaths.map(depPath => ({ id: depPath })),
        ...def
    });
}

/**
 * Adds common Employer field definitions
 * @param d
 */
export function addEmployerDefinitions(d: IDefinition): void {
    const pathToCompany = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.Company);
    addFormDef(d, createPath(pathToCompany, PrCSSZSubmissionCompanyEntity.LegalName), {
        width: BasicInputSizes.XL
    });
    addFormDef(d, createPath(pathToCompany, PrCSSZSubmissionCompanyEntity.LegalNumber), {});
    addFormDef(d, createPath(pathToCompany, PrCSSZSubmissionCompanyEntity.CSSZSymbolVariable), {});
    addFormDef(d, createPath(pathToCompany, PrCSSZSubmissionCompanyEntity.SocialSecurity), {
        ...getSocialSecurityFieldDef(),
        label: i18next.t("Submission:Form.SocialSecurity")
    });
    addFormGroup(d, {
        id: PrSubmissionGroup.Employer,
        title: i18next.t("Submission:Form.EmployerIdentifier"),
        rows: [[
            { id: createPath(pathToCompany, PrCSSZSubmissionCompanyEntity.LegalName) },
            { id: createPath(pathToCompany, PrCSSZSubmissionCompanyEntity.LegalNumber) },
            { id: createPath(pathToCompany, PrCSSZSubmissionCompanyEntity.CSSZSymbolVariable) },
            { id: createPath(pathToCompany, PrCSSZSubmissionCompanyEntity.SocialSecurity) }
        ]]
    });
}

interface ISubmissionDefArgs {
    withEmployment?: boolean;
    withAccount?: boolean;
    withoutEmployeeContact?: boolean;
    separateEmployeeIdentifiers?: boolean;
    isSingleEmployeeView?: boolean;
    pathToEmployee?: string;
    useEuSocialInsuranceNumber?: boolean;
}

export function addEmployeeMasterField(d: IDefinition, args?: ISubmissionDefArgs): void {
    const pathToEmployee = args?.pathToEmployee ?? PrCSSZSubmissionEntity.Employee;
    const employeePath = createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.Employee);

    const mainGroup = getFormGroup(d, PrSubmissionGroup.Main);
    // Employee selection field when entity is new -> pick and connect the submission with particular employee
    const employeeDefinition = cloneDeep(employeeWithDependentEmploymentSelectDef);
    if (args?.isSingleEmployeeView) {
        // defaulted in Editable Window, cannot be changed
        employeeDefinition.isVisible = false;
    }
    employeeDefinition.fieldSettings.localDependentFields
        .push(...[
            {
                from: { id: PrEmployeeEntity.FirstName },
                to: { id: PrCSSZSubmissionEmployeeEntity.FirstName },
                navigateFrom: NavigationSource.Parent
            },
            {
                from: { id: PrEmployeeEntity.LastName },
                to: { id: PrCSSZSubmissionEmployeeEntity.LastName },
                navigateFrom: NavigationSource.Parent
            },
            {
                from: { id: PrEmployeeEntity.PersonalIdentificationNumber },
                to: { id: PrCSSZSubmissionEmployeeEntity.PersonalIdentificationNumber },
                navigateFrom: NavigationSource.Parent
            },
            {
                from: { id: PrEmployeeEntity.DateOfBirth },
                to: { id: PrCSSZSubmissionEmployeeEntity.DateOfBirth },
                navigateFrom: NavigationSource.Parent
            }
        ]);
    if (!args.withoutEmployeeContact) {
        employeeDefinition.fieldSettings.localDependentFields
            .push(...[
                {
                    from: { id: PrEmployeeEntity.PhoneNumber },
                    to: { id: PrCSSZSubmissionEmployeeEntity.PhoneNumber },
                    navigateFrom: NavigationSource.Parent
                },
                {
                    from: { id: PrEmployeeEntity.Email },
                    to: { id: PrCSSZSubmissionEmployeeEntity.Email },
                    navigateFrom: NavigationSource.Parent
                }
            ]);
    }
    if (args?.useEuSocialInsuranceNumber) {
        employeeDefinition.fieldSettings.localDependentFields.push({
            from: { id: PrEmployeeEntity.EuSocialInsuranceNumber },
            to: { id: PrCSSZSubmissionEmployeeEntity.EuSocialInsuranceNumber },
            navigateFrom: NavigationSource.Parent
        });
    }
    if (!employeeDefinition.fieldSettings.additionalProperties) {
        employeeDefinition.fieldSettings.additionalProperties = [];
    }
    employeeDefinition.fieldSettings.additionalProperties.push(...[
        { id: PrEmployeeEntity.PersonalIdentificationNumber },
        { id: PrEmployeeEntity.DateOfBirth },
        ...(args?.useEuSocialInsuranceNumber ? [] : [{ id: PrEmployeeEntity.EuSocialInsuranceNumber }]),
        ...(args?.withoutEmployeeContact ? [] : [
            { id: PrEmployeeEntity.PhoneNumber },
            { id: PrEmployeeEntity.Email }
        ]),
        ...(args?.withAccount ? [{
            id: PrEmployeeEntity.BankAccount,
            additionalProperties: [
                { id: PrEmployeeBankAccountEntity.AccountNumber },
                { id: PrEmployeeBankAccountEntity.AbaNumber },
                { id: PrEmployeeBankAccountEntity.BankCode },
                { id: PrEmployeeBankAccountEntity.CountryCode },
                { id: PrEmployeeBankAccountEntity.IBAN },
                { id: PrEmployeeBankAccountEntity.SWIFT },
            ]
        }] : [])
    ]);

    addFormDef(d, employeePath, employeeDefinition);
    mainGroup.rows = [[
        { id: employeePath },
    ]];

    if (args?.withEmployment) {
        const employmentPath = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.Employment, PrCSSZSubmissionEmploymentEntity.Employment);

        const employmentDefinition = getEmploymentSelectDef(employeePath);
        employmentDefinition.fieldSettings.localDependentFields
            .push(...[
                {
                    from: { id: PrEmploymentEntity.DateStart },
                    to: { id: PrCSSZSubmissionEmploymentEntity.DateStart },
                    navigateFrom: NavigationSource.Parent
                },
                {
                    from: { id: PrEmploymentEntity.DateEnd },
                    to: { id: PrCSSZSubmissionEmploymentEntity.DateEnd },
                    navigateFrom: NavigationSource.Parent
                },
                {
                    from: { id: PrEmploymentEntity.CsszEmploymentType },
                    to: { id: PrCSSZSubmissionEmploymentEntity.CsszEmploymentType },
                    navigateFrom: NavigationSource.Parent
                },
                {
                    from: { id: PrEmploymentEntity.Type },
                    to: { id: PrCSSZSubmissionEmploymentEntity.Type },
                    navigateFrom: NavigationSource.Parent
                }
            ]);
        if (!employmentDefinition.fieldSettings.additionalProperties) {
            employmentDefinition.fieldSettings.additionalProperties = [];
        }
        employmentDefinition.fieldSettings.additionalProperties.push(...[
            { id: PrEmploymentEntity.DateStart },
            { id: PrEmploymentEntity.DateEnd },
            { id: PrEmploymentEntity.CsszEmploymentType },
            { id: PrEmploymentEntity.Type },
        ]);
        addFormDef(d, employmentPath, employmentDefinition);

        mainGroup.rows[0].push({ id: employmentPath });
    }

    addFormGroup(d, {
        id: PrSubmissionGroup.Employee,
        title: i18next.t("Submission:Form.EmployeeIdentifier"),
        rows: addEmployeeDefinitions(d, args)
    });
}

/**
 * Adds common Care Receiver field definitions, just returns care receiver rows to be extended and added to group
 * @param d
 * @param args customize definitions
 */
export function addEmployeeDefinitions(d: IDefinition, args: ISubmissionDefArgs): IFieldDef[][] {
    const pathToEmployee = args?.pathToEmployee ?? PrCSSZSubmissionEntity.Employee;

    addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.FirstName), {
        groupedField: GroupedField.MultiStart,
        isRequired: true
    });
    addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.LastName), {
        groupedField: GroupedField.MultiEnd,
        isRequired: true
    });
    if (!args?.separateEmployeeIdentifiers) {
        addFormDef(d, createPath(pathToEmployee, IdentificationTypeLocalPath), {
            ...getIdentificationTypeSelectDef([args.useEuSocialInsuranceNumber ? IdentificationType.DateOfBirth : IdentificationType.EuSocialInsuranceNumber]),
            groupedField: GroupedField.MultiStart,
            affectedFields: [
                { id: createPath(pathToEmployee, args.useEuSocialInsuranceNumber ? PrCSSZSubmissionEmployeeEntity.EuSocialInsuranceNumber : PrCSSZSubmissionEmployeeEntity.DateOfBirth) },
                { id: createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.PersonalIdentificationNumber) }
            ]
        });
    }
    if (args.useEuSocialInsuranceNumber) {
        addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.EuSocialInsuranceNumber), {
            labelStatus: LabelStatus.Hidden,
            groupedField: (args) =>
                isOfIdentType(IdentificationType.EuSocialInsuranceNumber)(args) ? GroupedField.MultiEnd : undefined,
            isRequired: true,
            isVisible: isOfIdentType(IdentificationType.EuSocialInsuranceNumber)
        });
    } else {
        addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.DateOfBirth), {
            ...(args?.separateEmployeeIdentifiers ? {} : {
                labelStatus: LabelStatus.Hidden,
                groupedField: (args) =>
                    isOfIdentType(IdentificationType.DateOfBirth)(args) ? GroupedField.MultiEnd : undefined,
                isRequired: true,
                isVisible: isOfIdentType(IdentificationType.DateOfBirth)
            })
        });
    }
    addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.PersonalIdentificationNumber), {
        ...BirthNumberFieldDef,
        ...(args?.separateEmployeeIdentifiers ? {} : {
            labelStatus: LabelStatus.Hidden,
            groupedField: (args) =>
                isOfIdentType(IdentificationType.IdentificationNumber)(args) ? GroupedField.MultiEnd : undefined,
            isRequired: true,
            isVisible: isOfIdentType(IdentificationType.IdentificationNumber)
        })
    });
    if (!args?.withoutEmployeeContact) {
        addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.PhoneNumber), {
            ...PhoneNumberDef
        });
        addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.Email), {
            ...EmailDef
        });
    }

    const rows = [[
        { id: createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.FirstName) },
        { id: createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.LastName) },
        ...(args?.separateEmployeeIdentifiers ? [] : [{ id: createPath(pathToEmployee, IdentificationTypeLocalPath) }]),
        { id: createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.PersonalIdentificationNumber) },
        { id: createPath(pathToEmployee, args.useEuSocialInsuranceNumber ? PrCSSZSubmissionEmployeeEntity.EuSocialInsuranceNumber : PrCSSZSubmissionEmployeeEntity.DateOfBirth) }
    ]];

    if (!args?.withoutEmployeeContact) {
        rows[0].push(
            { id: createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.PhoneNumber) },
            { id: createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.Email) }
        );
    }

    if (args?.withEmployment) {
        const pathToEmployment = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.Employment);

        // Employment related fields
        addFormDef(d, createPath(pathToEmployment, PrCSSZSubmissionEmploymentEntity.DateStart), {
            groupedField: GroupedField.MultiStart,
            label: i18next.t("Submission:SocialEventsForm.EmploymentDateStart")
        });
        addFormDef(d, createPath(pathToEmployment, PrCSSZSubmissionEmploymentEntity.DateEnd), {
            groupedField: GroupedField.MultiEnd,
            label: i18next.t("Submission:SocialEventsForm.EmploymentDateEnd"),
            fieldSettings: {
                showSpecialValue: DateRangeSpecialValue.WithoutEnd
            },
        });
        addFormDef(d, createPath(pathToEmployment, PrCSSZSubmissionEmploymentEntity.CsszEmploymentType), {
            ...getEmploymentTypeDef(createPath(pathToEmployment, PrCSSZSubmissionEmploymentEntity.Type)),
            label: i18next.t("Submission:SocialEventsForm.CsszEmploymentType"),
        });

        rows.push([
            { id: createPath(pathToEmployment, PrCSSZSubmissionEmploymentEntity.DateStart) },
            { id: createPath(pathToEmployment, PrCSSZSubmissionEmploymentEntity.DateEnd) },
            { id: createPath(pathToEmployment, PrCSSZSubmissionEmploymentEntity.CsszEmploymentType) }
        ]);
    }

    return rows;
}

function getSocialEventSummaryItemDef() {
    return {
        id: PrSubmissionEntity.SocialEvent,
        label: i18next.t("Submission:Form.SocialEvent"),
        formatter: (val: TValue, args: IFormatOptions) => {
            const { bindingContext } = args.storage.data;
            const socialEvent = getBoundValue({
                data: args.entity,
                dataBindingContext: bindingContext,
                bindingContext: bindingContext.navigate(PrSubmissionEntity.SocialEvent)
            }) as IPrSocialEventEntity;
            return socialEvent?.NumberOurs ?? DASH_CHARACTER;
        },
        additionalProperties: [
            { id: PrSocialEventEntity.NumberOurs }
        ]
    };
}

export function addCommonNEMPRIDefinitions(d: IDefinition, context: IAppContext, args: IGetSubmissionDefArgs): void {

    // extend Summary header
    d.form.summary.splice(1, 0, getSocialEventSummaryItemDef());

    d.form.additionalProperties = [
        ...d.form.additionalProperties,
        { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.Company, PrCSSZSubmissionCompanyEntity.Company, CompanyEntity.Id) }
    ];

    // **************** identifier group ****************
    const dependentPaths = [
        createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisionNumber)
    ];
    addCSSZSwitchDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.IsForeigner), null, dependentPaths, {
        customizationData: {
            // use dependents to don't make it jumpy - switch keeps padding
            dependents: [...dependentPaths]
        },
    });
    addCSSZSwitchDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.IsCorrectiveSubmission));
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisionNumber), {
        label: i18next.t("Submission:SocialEventsForm.DecisionNumber"),
        width: BasicInputSizes.XL,
        isRequired: true,
        isVisible: ({ storage }: IGetValueArgs) => {
            const isForeigner = storage.getValueByPath(createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.IsForeigner));
            return isForeigner !== true;
        }
    });
    addFormGroup(d, {
        id: PrSubmissionGroup.Identifier,
        title: i18next.t("Submission:SocialEventsForm.Identifier"),
        rows: [[
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.IsForeigner) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.IsCorrectiveSubmission) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisionNumber) },
        ]]
    });

    // **************** Add company / employer fields ****************
    addEmployerDefinitions(d);

    // **************** Add Employee related fields ****************
    addEmployeeMasterField(d, {
        withEmployment: true,
        withAccount: true,
        isSingleEmployeeView: args?.isSingleEmployeeView,
        useEuSocialInsuranceNumber: true
    });

    // **************** Add IncomeInPeriod group ****************
    addFormDef(d, IncomeTypeLocalPath, {
        type: FieldType.SegmentedButton,
        labelStatus: LabelStatus.Removed,
        fieldSettings: {
            items: [
                {
                    id: CSSZSubmissionIncomeType.DecisivePeriod,
                    label: i18next.t("Submission:SocialEventsForm.IncomeType_DecisivePeriod")
                },
                {
                    id: CSSZSubmissionIncomeType.EstimatedIncomeAmount,
                    label: i18next.t("Submission:SocialEventsForm.IncomeType_EstimatedIncomeAmount")
                },
            ]
        },
        affectedFields: [
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DateDecisivePeriodStart) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DateDecisivePeriodEnd) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisivePeriods) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.EstimatedIncomeAmount) }
        ]
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DateDecisivePeriodStart), {
        type: FieldType.Date,
        label: i18next.t("Submission:SocialEventsForm.From"),
        defaultValue: () => getUtcDayjs().subtract(1, "year").startOf("month").toDate(),
        groupedField: GroupedField.MultiStart,
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DateDecisivePeriodEnd), {
        type: FieldType.Date,
        label: i18next.t("Submission:SocialEventsForm.To"),
        defaultValue: () => getUtcDayjs().subtract(1, "month").endOf("month").toDate(),
        groupedField: GroupedField.MultiEnd,
    });
    const decisivePeriodColumns: IFieldDef[] = [
        { id: PrCSSZDecisivePeriodEntity.Month },
        { id: PrCSSZDecisivePeriodEntity.ExcludedDays },
        { id: PrCSSZDecisivePeriodEntity.IncludableIncomeAmount }
    ];
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisivePeriods, PrCSSZDecisivePeriodEntity.Month), {
        isReadOnly: true,
        formatter: (val, { bindingContext, storage }: IFormatOptions) => {
            if (bindingContext.getParent().getKey() === SummaryRowId) {
                return strongTextFormatter(i18next.t("Submission:SocialEventsForm.DecisivePeriodsSummary").toString());
            }
            if (!val) {
                return DASH_CHARACTER;
            }
            const year = storage.getValue(bindingContext.getParent().navigate(PrCSSZDecisivePeriodEntity.Year));
            const date = getUtcDayjs()
                .set("month", (val as number) - 1)
                .set("year", year as number);
            return capitalize(DateType.format(date, DateFormat.monthAndYear));
        },
        additionalProperties: [{ id: `/${createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisivePeriods, PrCSSZDecisivePeriodEntity.Year)}` }]
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisivePeriods, PrCSSZDecisivePeriodEntity.ExcludedDays), {
        width: FastEntryInputSizes.S,
        isRequired: true,
        defaultValue: 0,
        isReadOnly: isSummaryRow,
        formatter: smartStrongTextFormatter,
        useForValidation: true // field added as custom render, needs to be marked for validation manually
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisivePeriods, PrCSSZDecisivePeriodEntity.IncludableIncomeAmount), {
        width: FastEntryInputSizes.M,
        isRequired: true,
        isReadOnly: isSummaryRow,
        formatter: (val, args) => {
            // combines currency formatting with summary row formatting
            return getPayrollCurrencyFormatter()(val, args);
        },
        fieldSettings: {
            unit: getPayrollCurrencyUnit()
        },
        useForValidation: true // field added as custom render, needs to be marked for validation manually
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisivePeriods), {
        type: FieldType.Custom,
        labelStatus: LabelStatus.Removed,
        isVisible: (args: IGetValueArgs) => !!args.storage.data.entity.Detail?.DecisivePeriods?.length,
        render({ props }) {
            return (
                <DecisivePeriodsSmartFastEntryList {...props}
                                                   columns={decisivePeriodColumns}
                                                   groupDefinitionId={PrSubmissionGroup.IncomeInPeriod}/>
            );
        },
        customizationData: {
            useForCustomization: false
        }
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.EstimatedIncomeAmount), {
        ...getPayrollCurrencyFieldDef(),
        width: BasicInputSizes.L,
        isRequired: true,
        clearIfInvisible: false,
        isVisible: isIncomeType(CSSZSubmissionIncomeType.EstimatedIncomeAmount),
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.ExcludedDays), {
        // just a spacer for excluded days summary (calculated from decisive periods)
        clearIfInvisible: false,
        isVisible: false,
    });

    addCSSZSwitchDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.HasWorkedOnEventStartDate), "HasWorkedOnEventStartDate", [
        createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.ShiftLengthOnEventStartDate),
        createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.WorkedHoursOnEventStartDate)
    ]);
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.ShiftLengthOnEventStartDate), {
        label: i18next.t("Submission:SocialEventsForm.ShiftLengthOnEventStartDate"),
        width: BasicInputSizes.L,
        isRequired: true,
        isVisible: workedOnEventStartDate,
        fieldSettings: {
            unit: "h"
        }
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.WorkedHoursOnEventStartDate), {
        label: i18next.t("Submission:SocialEventsForm.WorkedHoursOnEventStartDate"),
        width: BasicInputSizes.S,
        isRequired: true,
        isVisible: workedOnEventStartDate,
        fieldSettings: {
            unit: "h"
        }
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.IncomeFromSmallScaleEmployment), {
        ...getPayrollCurrencyFieldDef(),
        label: i18next.t("Submission:SocialEventsForm.IncomeFromSmallScaleEmployment"),
        width: BasicInputSizes.XL,
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.IncomeInPeriod,
        title: i18next.t("Submission:SocialEventsForm.IncomeInPeriod"),
        rows: [[
            { id: IncomeTypeLocalPath }
        ], [
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DateDecisivePeriodStart) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DateDecisivePeriodEnd) }
        ], [
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.EstimatedIncomeAmount) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisivePeriods) },
        ], [
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.HasWorkedOnEventStartDate) },
        ], [
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.ShiftLengthOnEventStartDate) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.WorkedHoursOnEventStartDate) }
        ], [
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionMaternityLeaveDetailEntity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.IncomeFromSmallScaleEmployment) }
        ]]
    });

    // **************** Payment method ****************
    // Address fields
    const addressFields = preparePaymentMethodAddressFieldDefs(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentConnectionDelivery), PrCSSZPaymentMethodCode.PaymentToAddress);

    // Bank account fields
    const baseBankAccountDefinitions = getBankAccountsFieldBaseDef();

    // Bank account fields - CZ
    const bankAccountFields: { id: string }[] = [];
    forEachKey(baseBankAccountDefinitions, (fieldId) => {
        const path = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferCZ, fieldId);
        addFormDef(d, path, baseBankAccountDefinitions[fieldId]);
        enhanceFieldVisibility(d, path, isPaymentType(PrCSSZPaymentMethodCode.PaymentToAccountInTheCzechRepublic));
        bankAccountFields.push({ id: path });
    });
    // country field is not visible, always CZ for this payment method
    const bankCountryCZPath = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferCZ, PrCSSZPaymentConnectionWireTransferCZEntity.Country);
    enhanceFieldVisibility(d, bankCountryCZPath, false, false);

    const ssPath = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferCZ, PrCSSZPaymentConnectionWireTransferCZEntity.SymbolSpecific);
    addFormDef(d, ssPath, {
        ...SymbolSpecificDef,
        isVisible: isPaymentType(PrCSSZPaymentMethodCode.PaymentToAccountInTheCzechRepublic)
    });
    bankAccountFields.push({ id: ssPath });

    // Bank account fields - ABROAD
    forEachKey(baseBankAccountDefinitions, (fieldId) => {
        const path = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferAbroad, fieldId);
        if (fieldId === PrCSSZPaymentConnectionWireTransferAbroadEntity.Country) {
            // customize country definition
            addFormDef(d, path, {
                ...baseBankAccountDefinitions[fieldId],
                defaultValue: CountryCode.Slovakia,
                fieldSettings: {
                    ...baseBankAccountDefinitions[fieldId].fieldSettings,
                    transformFetchedItems: (items) => {
                        return items.filter(i => i.id !== CountryCode.CzechRepublic);
                    }
                },
                affectedFields: [
                    ...baseBankAccountDefinitions[fieldId].affectedFields || [],
                    { id: PrCSSZPaymentConnectionWireTransferAbroadEntity.PostalCode, navigateFromParent: true }
                ]
            });
        } else {
            addFormDef(d, path, baseBankAccountDefinitions[fieldId]);
        }
        enhanceFieldVisibility(d, path, isPaymentType(PrCSSZPaymentMethodCode.PaymentToAccountAbroad));
        bankAccountFields.push({ id: path });
    });
    // AccountName, BankName
    const accountNamePath = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferAbroad, PrCSSZPaymentConnectionWireTransferAbroadEntity.AccountName);
    addFormDef(d, accountNamePath, {
        isVisible: isPaymentType(PrCSSZPaymentMethodCode.PaymentToAccountAbroad)
    });
    const bankNamePath = createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferAbroad, PrCSSZPaymentConnectionWireTransferAbroadEntity.BankName);
    addFormDef(d, bankNamePath, {
        width: BasicInputSizes.L,
        isVisible: isPaymentType(PrCSSZPaymentMethodCode.PaymentToAccountAbroad)
    });

    addressFields.push(...preparePaymentMethodAddressFieldDefs(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentConnectionWireTransferAbroad), PrCSSZPaymentMethodCode.PaymentToAccountAbroad));
    bankAccountFields.push({ id: accountNamePath }, { id: bankNamePath });

    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentMethod), {
        type: FieldType.ComboBox,
        label: i18next.t("Submission:SocialEventsForm.PaymentMethod"),
        width: BasicInputSizes.L,
        fieldSettings: {
            displayName: PrCSSZPaymentMethodEntity.Name,
            noRecordText: i18next.t("Common:Select.NoRecord"),
            transformFetchedItems: (items) => {
                return items.map(item =>
                    ({ ...item, label: i18next.t(`Submission:PaymentMethod.${item.id}`) })
                );
            }
        },
        affectedFields: [
            ...addressFields,
            ...bankAccountFields,
        ]
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.PaymentMethod,
        rows: [[
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.PaymentMethod) }
        ], [
            ...bankAccountFields
        ], [
            ...addressFields
        ]]
    });

    // **************** AdditionalInfo ****************
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.AdditionalInfo), {
        type: FieldType.EditableText,
        labelStatus: LabelStatus.Removed,
        isConfirmable: true,
        width: EditableTextSizes.M,
        fieldSettings: {
            placeholder: i18next.t("Submission:SocialEventsForm.AddAdditionalInfo")
        }
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.AdditionalInfo,
        title: i18next.t("Submission:SocialEventsForm.AdditionalInfo"),
        rows: [[
            { id: createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.AdditionalInfo) }
        ]]
    });

    // **************** Attachments ****************
    addFormDef(d, PrSubmissionEntity.ElectronicAttachments, {
        label: i18next.t("Submission:SocialEventsForm.ElectronicAttachmentsLabel"),
        ...formAttachmentFieldDef
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Attachments,
        title: i18next.t("Submission:SocialEventsForm.Attachments"),
        rows: [[
            { id: PrSubmissionEntity.ElectronicAttachments }
        ]]
    });

    // **************** CSSZ tabs ****************
    addFormGroup(d, {
        id: PrSubmissionGroup.Tabs,
        isVisible: not(isNewEntity),
        // tabs: [{
        //     id: EntitySetName.PrSocialEvents,
        //     title: i18next.t("Banks:Transactions.TabAssignment"),
        //     table: // todo: ...
        // }]
    });
}


/**
 * **************** Pension defs ****************
 */
export const HasPensionLocalPath = BindingContext.localContext("hasPensionLocalPath");

export function initHasPensionLocalPath(storage: FormStorage): void {
    const entity = storage.data.entity as IPrCSSZSubmissionMaternityLeaveEntity;
    const pensionType = entity?.Detail?.EmployerConfirmation?.PensionTypeCode as PrPensionTypeCode;
    storage.setValueByPath(HasPensionLocalPath, !!pensionType && pensionType !== PrPensionTypeCode.DoesNotReceive);
}

export function pensionBeforeSaveAdjustments<T extends IPrCSSZSubmissionMaternityLeaveEntity | IPrCSSZSubmissionIllnessLeaveEntity>(data: T, storage: FormStorage): T {
    if (!storage.getValueByPath(HasPensionLocalPath)) {
        if (!data?.Detail?.EmployerConfirmation) {
            data.Detail.EmployerConfirmation = {};
        }
        data.Detail.EmployerConfirmation.PensionTypeCode = PrPensionTypeCode.DoesNotReceive;
    }
    return data;
}

export function addPensionFieldsDefinitions(d: IDefinition, Entity: typeof PrCSSZSubmissionMaternityLeaveDetailEntity | typeof PrCSSZSubmissionIllnessLeaveDetailEntity): IFormGroupDef["rows"] {
    addCSSZSwitchDef(d, HasPensionLocalPath, "HasPension", [
        createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.PensionType)
    ]);
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.PensionType), {
        type: FieldType.ComboBox,
        isRequired: true,
        defaultValue: PrPensionTypeCode.DoesNotReceive,
        isVisible: ({ storage }) => !!storage.getValueByPath(HasPensionLocalPath),
        clearIfInvisible: false,
        fieldSettings: {
            displayName: PrPensionTypeEntity.Name,
            transformFetchedItems: (items: ISelectItem[]): ISelectItem[]  => {
                return items.filter(i => i.id !== PrPensionTypeCode.DoesNotReceive);
            }
        }
    });

    return [[
        { id: HasPensionLocalPath }
    ], [
        { id: createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.PensionType) }
    ]];
}

/**
 * **************** Maternity related fields defs ****************
 */
interface IMaternityRelatedFieldArgs {
    enhanced: boolean;
}

export function addMaternityRelatedFieldDefinitions(d: IDefinition, Entity: typeof PrCSSZSubmissionMaternityLeaveDetailEntity | typeof PrCSSZSubmissionIllnessLeaveDetailEntity | typeof PrCSSZSubmissionCaregivingDetailEntity | typeof PrCSSZSubmissionLongtermCaregivingDetailEntity, args?: IMaternityRelatedFieldArgs): IFormGroupDef["rows"] {
    const statusPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.SubsequentMaternityLeaveStatus);
    const datePreviousChildBirthPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.DatePreviousChildBirth);
    const isTransferredToOtherWorkPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.IsTransferredToOtherWork);
    const dateTransferToOtherWorkPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.DateTransferToOtherWork);

    if (args?.enhanced) {
        addFormDef(d, statusPath, {
            type: FieldType.ComboBox,
            width: BasicInputSizes.XL,
            label: i18next.t("Submission:SocialEventsForm.SubsequentMaternityLeaveStatus"),
            fieldSettings: {
                displayName: PrSubsequentMaternityLeaveStatusEntity.Name,
                noRecordText: i18next.t("Common:Select.NoRecord"),
                transformFetchedItems: (items: ISelectItem[]): ISelectItem[]  => {
                    return items.map(i => ({
                        ...i,
                        label: i18next.t(`Submission:SubsequentMaternityLeaveStatus.${i.id}`)
                    }));
                }
            },
            customizationData: {
                dependents: [datePreviousChildBirthPath]
            },
            affectedFields: [{ id: datePreviousChildBirthPath }]
        });
        addFormDef(d, datePreviousChildBirthPath, {
            label: i18next.t("Submission:SocialEventsForm.DatePreviousChildBirth"),
            isRequired: true,
            isVisible: ({ storage }) =>
                storage.getValueByPath(createPath(statusPath, PrSubsequentMaternityLeaveStatusEntity.Code)) === PrSubsequentMaternityLeaveStatusCode.Starting,
        });
    }

    addCSSZSwitchDef(d, isTransferredToOtherWorkPath, "IsTransferredToOtherWork", [dateTransferToOtherWorkPath]);
    addFormDef(d, dateTransferToOtherWorkPath, {
        label: i18next.t("Submission:SocialEventsForm.DateTransferToOtherWork"),
        isRequired: true,
        isVisible: ({ storage }) =>
            !!storage.getValueByPath(isTransferredToOtherWorkPath),
    });

    return [
        ...(args?.enhanced ? [[
            { id: statusPath },
            { id: datePreviousChildBirthPath },
        ]] : []),
        [
            { id: isTransferredToOtherWorkPath },
            { id: dateTransferToOtherWorkPath },
        ]
    ];
}

/**
 * **************** Study fields ****************
 */
export function addStudyFieldDefinitions(d: IDefinition, Entity: typeof PrCSSZSubmissionIllnessLeaveDetailEntity | typeof PrCSSZSubmissionCaregivingDetailEntity | typeof PrCSSZSubmissionLongtermCaregivingDetailEntity, args?: IMaternityRelatedFieldArgs): IFormGroupDef["rows"] {
    const isStudentPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationCaregivingEntity.IsStudent);
    const isEmploymentInHolidaysPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationCaregivingEntity.IsEmploymentInHolidays);

    addCSSZSwitchDef(d, isStudentPath, "IsStudent", [isEmploymentInHolidaysPath]);
    addCSSZSwitchDef(d, isEmploymentInHolidaysPath, "IsEmploymentInHolidays", {
        isVisible: ({ storage, bindingContext }) =>
            !!storage.getValue(bindingContext.getParent().navigate(PrCSSZSubmissionEmployerConfirmationCaregivingEntity.IsStudent))
    });

    return [[
        { id: isStudentPath },
        { id: isEmploymentInHolidaysPath },
    ]];
}

/**
 * **************** Distrainment and Insolvency fields ****************
 */
export function distrainmentAndInsolvencyFields(d: IDefinition, Entity: typeof PrCSSZSubmissionMaternityLeaveDetailEntity | typeof PrCSSZSubmissionIllnessLeaveDetailEntity): IFormGroupDef["rows"] {
    const distrainmentPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.HasDistrainment);
    const insolvencyPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationMaternityLeaveEntity.HasInsolvency);
    addCSSZSwitchDef(d, distrainmentPath, "HasDistrainment");
    addCSSZSwitchDef(d, insolvencyPath, "HasInsolvency");

    return [[
        { id: distrainmentPath },
        { id: insolvencyPath },
    ]];
}

/**
 * **************** WorkLeave without pay fields defs ****************
 */
const hasWorkLeaveWithoutPay: TGetValueFn<boolean> = ({ storage, bindingContext }) =>
    !!storage.getValue(bindingContext.getParent().navigate(PrCSSZSubmissionEmployerConfirmationCaregivingEntity.HasWorkLeaveWithoutPay));

interface IWorkLeaveWithoutPayArgs {
    customWorkFieldLabelKey: string;
}
export function addWorkLeaveWithoutPayFieldDefinitions(d: IDefinition, Entity: typeof PrCSSZSubmissionIllnessLeaveDetailEntity | typeof PrCSSZSubmissionCaregivingDetailEntity | typeof PrCSSZSubmissionLongtermCaregivingDetailEntity, args?: IWorkLeaveWithoutPayArgs): IFormGroupDef["rows"] {
    const hasWorkLeaveWithoutPayPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationCaregivingEntity.HasWorkLeaveWithoutPay);
    const workLeaveWithoutPayFromPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationCaregivingEntity.DateWorkLeaveWithoutPayFrom);
    const workLeaveWithoutPayToPath = createPath(PrCSSZSubmissionEntity.Detail, Entity.EmployerConfirmation, PrCSSZSubmissionEmployerConfirmationCaregivingEntity.DateWorkLeaveWithoutPayTo);

    addCSSZSwitchDef(d, hasWorkLeaveWithoutPayPath, args?.customWorkFieldLabelKey ?? "HasWorkLeaveWithoutPay", [
        workLeaveWithoutPayFromPath, workLeaveWithoutPayToPath
    ]);
    addFormDef(d, workLeaveWithoutPayFromPath, {
        type: FieldType.Date,
        label: i18next.t("Submission:SocialEventsForm.From"),
        groupedField: GroupedField.MultiStart,
        isRequired: hasWorkLeaveWithoutPay,
        isVisible: hasWorkLeaveWithoutPay
    });
    addFormDef(d, workLeaveWithoutPayToPath, {
        type: FieldType.Date,
        label: i18next.t("Submission:SocialEventsForm.To"),
        groupedField: GroupedField.MultiEnd,
        isRequired: hasWorkLeaveWithoutPay,
        isVisible: hasWorkLeaveWithoutPay
    });

    return [[
        { id: hasWorkLeaveWithoutPayPath },
        { id: workLeaveWithoutPayFromPath },
        { id: workLeaveWithoutPayToPath }
    ]];
}

/**
 * **************** Caregiving actions definitions ****************
 */
type TTypeProp =
    PrCSSZSubmissionCaregivingDetailEntity.IsStart
    | PrCSSZSubmissionCaregivingDetailEntity.IsDuration
    | PrCSSZSubmissionCaregivingDetailEntity.IsEnd;
const isOfType = (prop: TTypeProp): TGetValueFn<boolean> =>
    ({ storage }) =>
        !!storage.getValue(storage.data.bindingContext
            .navigate(PrCSSZSubmissionCaregivingEntity.Detail)
            .navigate(prop));

export const isStartType = isOfType(PrCSSZSubmissionCaregivingDetailEntity.IsStart);
export const isDurationType = isOfType(PrCSSZSubmissionCaregivingDetailEntity.IsDuration);
export const isEndType = isOfType(PrCSSZSubmissionCaregivingDetailEntity.IsEnd);

export function clearEntityAccordingToActionType<T extends (IPrCSSZSubmissionLongtermCaregivingEntity | IPrCSSZSubmissionCaregivingEntity)>(data: T, storage: FormStorage): T {
    const _isStart = isStartType({ storage });
    const _isDuration = isDurationType({ storage });
    const _isEnd = isEndType({ storage });

    if (!_isStart) {
        data.Detail.EmployerConfirmation = null;
        data.Detail.CareReceiver = null;
    }

    if (!(_isDuration || _isEnd)) {
        data.Detail.PaymentBasis = null;
    } else if (isObjectEmpty(data.Detail.PaymentBasis)) {
        // PaymentBasis is mandatory even if no data is entered
        // so we need to fill some data to avoid validation errors
        data.Detail.PaymentBasis = {
            [PrCSSZSubmissionPaymentBasisEntity.HasPlannedShifts]: false
        };
    }

    return data;
}

export function addCaregivingActions(d: IDefinition, Entity: typeof PrCSSZSubmissionCaregivingDetailEntity | typeof PrCSSZSubmissionLongtermCaregivingDetailEntity): void {
    // **************** CareGiving actions ****************
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, Entity.IsStart), {
        type: FieldType.Checkbox,
        labelStatus: LabelStatus.Removed,
        defaultValue: false,
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, Entity.IsDuration), {
        type: FieldType.Checkbox,
        labelStatus: LabelStatus.Removed,
        defaultValue: false,
    });
    addFormDef(d, createPath(PrCSSZSubmissionEntity.Detail, Entity.IsEnd), {
        type: FieldType.Checkbox,
        labelStatus: LabelStatus.Removed,
        defaultValue: false,
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.CaregivingEvents,
        title: i18next.t("Submission:SocialEventsForm.Actions"),
        rows: [[
            { id: createPath(PrCSSZSubmissionEntity.Detail, Entity.IsStart) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, Entity.IsDuration) },
            { id: createPath(PrCSSZSubmissionEntity.Detail, Entity.IsEnd) }
        ]]
    }, PrSubmissionGroup.Main);
}

export function handleCaregivingActionsChange(e: ISmartFieldChange, storage: FormStorage): void {
    const path = e.bindingContext.getPath();

    if ([PrCSSZSubmissionCaregivingDetailEntity.IsStart, PrCSSZSubmissionCaregivingDetailEntity.IsDuration, PrCSSZSubmissionCaregivingDetailEntity.IsEnd]
        .includes(path as PrCSSZSubmissionCaregivingDetailEntity)) {
        storage.refresh(true);
    }
}

/**
 * **************** Return to work fields and definitions ****************
 */
export const ReturnToWorkLocalContext = BindingContext.localContext("ReturnToWorkLocalContext");

export function initReturnToWorkLocalContext(storage: FormStorage): void {
    const entity = storage.data.entity as IPrCSSZSubmissionPaternityLeaveEntity;
    storage.setValueByPath(ReturnToWorkLocalContext, !!entity?.Detail?.PaymentBasis?.ShiftLengthOnLastDayOfSupportPeriod);
}

export const isReturnToWorkVisible: TGetValueFn<boolean> = ({ storage }) =>
    !!storage.getValueByPath(ReturnToWorkLocalContext);

interface IReturnToWorkArgs {
    addDateReturnToWork: boolean;
}

export function addReturnToWorkFields(d: IDefinition, Entity: typeof PrCSSZSubmissionCaregivingDetailEntity | typeof PrCSSZSubmissionLongtermCaregivingDetailEntity | typeof PrCSSZSubmissionPaternityLeaveDetailEntity, args?: IReturnToWorkArgs): void {
    const _getPath = (prop: string) =>
        createPath(PrCSSZSubmissionEntity.Detail, Entity.PaymentBasis, prop);

    addCSSZSwitchDef(d, ReturnToWorkLocalContext, "ReturnToWorkLocalContextSwitch", [
        ...(args?.addDateReturnToWork ? [_getPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.DateReturnToWork)] : []),
        _getPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.ShiftLengthOnLastDayOfSupportPeriod),
        _getPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.WorkedHoursOnLastDayOfSupportPeriod)
    ]);
    if (args?.addDateReturnToWork) {
        addFormDef(d, _getPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.DateReturnToWork), {
            label: i18next.t("Submission:SocialEventsForm.DateReturnToWork"),
            isRequired: true,
            isVisible: isReturnToWorkVisible
        });
    }
    addFormDef(d, _getPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.ShiftLengthOnLastDayOfSupportPeriod), {
        label: i18next.t("Submission:SocialEventsForm.ShiftLengthOnLastDayOfSupportPeriod"),
        width: BasicInputSizes.L,
        isRequired: true,
        isVisible: isReturnToWorkVisible,
        fieldSettings: {
            unit: "h"
        }
    });
    addFormDef(d, _getPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.WorkedHoursOnLastDayOfSupportPeriod), {
        label: i18next.t("Submission:SocialEventsForm.WorkedHoursOnLastDayOfSupportPeriod"),
        width: BasicInputSizes.S,
        isRequired: true,
        isVisible: isReturnToWorkVisible,
        fieldSettings: {
            unit: "h"
        }
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.BenefitPaymentInputsReturnToWork,
        showGroupDivider: false,
        rows: [[
            { id: ReturnToWorkLocalContext }
        ], [
            ...(args?.addDateReturnToWork ? [{ id: _getPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.DateReturnToWork) }] : []),
            { id: _getPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.ShiftLengthOnLastDayOfSupportPeriod) },
            { id: _getPath(PrCSSZSubmissionPaymentBasisPaternityLeaveEntity.WorkedHoursOnLastDayOfSupportPeriod) },
        ]],
    }, PrSubmissionGroup.BenefitPaymentInputs);
}


/**
 * ****************** Benefit Request group *****************
 */
export function addBasicBenefitRequestGroup(d: IDefinition, rows: IFormGroupDef["rows"], customDef?: Partial<IFormGroupDef>): void {
    addFormGroup(d, {
        id: PrSubmissionGroup.BenefitRequest,
        title: i18next.t("Submission:SocialEventsForm.BenefitRequest"),
        isSmallBottomMargin: true,
        rows,
        ...customDef
    }, PrSubmissionGroup.PaymentMethod);
}

/**
 * ****************** CaregivingIntervals group *****************
 */
const DateEndStartCaregivingLabelLocalPath = BindingContext.localContext("DateEndStartCaregivingLabel");

interface ICaregivingIntervalArgs {
    labelPrefix: string
}

export function addNewCaregivingIntervalLineItem(storage: FormStorage, collectionBc: BindingContext): void {
    const detailBc = storage.data.bindingContext.navigate(PrCSSZSubmissionEntity.Detail);
    const columns: IFieldDef[] = [
        { id: PrSubmissionCaregivingIntervalEntity.DateFrom }, { id: PrSubmissionCaregivingIntervalEntity.DateTo }
    ];
    const groupId = storage._getGroupKeyFromBc(collectionBc.navigate(columns[0].id));
    const DateFrom = storage.getValue(detailBc.navigate(PrCSSZSubmissionCaregivingDetailEntity.DateStart));
    const DateTo = storage.getValue(detailBc.navigate(PrCSSZSubmissionCaregivingDetailEntity.DateEnd));
    addNewLineItem(storage, collectionBc.getNavigationPath(), groupId, columns, { DateFrom, DateTo });
}

export function handleIsCaringPersonallyChange(e: ISmartFieldChange, storage: FormStorage): void {
    if (e.bindingContext.getPath() === PrCSSZSubmissionCaregivingDetailEntity.IsCaringPersonally) {
        if (e.value) {
            // reduce caregiving intervals to one and set it to whole period if caring personally is selected
            if (storage.data.entity?.Detail?.CaregivingIntervals?.length) {
                storage.data.entity.Detail.CaregivingIntervals = [];
            }
            addNewCaregivingIntervalLineItem(storage, storage.data.bindingContext.navigate(createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionCaregivingDetailEntity.CaregivingIntervals)));
        }
        storage.refreshGroupByKey(PrSubmissionGroup.CaregivingIntervals);
    }
}

export function cleanUpCaregivingIntervalsBeforeSave<T extends (IPrCSSZSubmissionLongtermCaregivingEntity | IPrCSSZSubmissionCaregivingEntity)>(data: T, storage: FormStorage): T {
    if (!isVisibleByPath(storage, createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionLongtermCaregivingDetailEntity.CaregivingIntervals, PrSubmissionCaregivingIntervalEntity.DateFrom))) {
        data.Detail.CaregivingIntervals = [];
    }

    return data;
}

export function addCaregivingIntervalFields(d: IDefinition, Entity: typeof PrCSSZSubmissionCaregivingDetailEntity | typeof PrCSSZSubmissionLongtermCaregivingDetailEntity, opts: ICaregivingIntervalArgs): void {
    const _getPath = (path: PrCSSZSubmissionCaregivingDetailEntity | PrCSSZSubmissionLongtermCaregivingDetailEntity, ...restProps: string[]) =>
        createPath(PrCSSZSubmissionEntity.Detail, path, ...restProps);

    addFormDef(d, DateEndStartCaregivingLabelLocalPath, {
        labelStatus: LabelStatus.Removed,
        isVisible: ifAny(isStartType, isDurationType, isEndType),
        render: () => (
            <CareGivingLabelStyled>{i18next.t(`Submission:SocialEventsForm.${opts.labelPrefix}_DateEndStartLabel`)}</CareGivingLabelStyled>
        )
    });
    addFormDef(d, _getPath(Entity.DateStart), {
        groupedField: GroupedField.MultiStart,
        label: i18next.t("Submission:SocialEventsForm.From"),
        isRequired: isStartType,
        isVisible: isStartType
    });
    addFormDef(d, _getPath(Entity.DateEnd), {
        groupedField: GroupedField.MultiEnd,
        label: i18next.t("Submission:SocialEventsForm.To"),
        isRequired: ifAny(isDurationType, isEndType),
        isVisible: ifAny(isDurationType, isEndType)
    });

    addCSSZSwitchDef(d, _getPath(Entity.IsCaringPersonally), `${opts.labelPrefix}_IsCaringPersonally`, {
        isVisible: ifAny(isDurationType, isEndType)
    });
    addFormDef(d, _getPath(Entity.CaregivingIntervals, PrSubmissionCaregivingIntervalEntity.DateFrom), {
        label: i18next.t("Submission:SocialEventsForm.From"),
        isVisible: ifAny(isDurationType, isEndType)
    });
    addFormDef(d, _getPath(Entity.CaregivingIntervals, PrSubmissionCaregivingIntervalEntity.DateTo), {
        label: i18next.t("Submission:SocialEventsForm.To"),
        isVisible: ifAny(isDurationType, isEndType)
    });
    addFormGroup(d, {
        id: PrSubmissionGroup.CaregivingIntervals,
        isSmallBottomMargin: true,
        rows: [
            [{ id: DateEndStartCaregivingLabelLocalPath }],
            [
                { id: _getPath(Entity.DateStart) },
                { id: _getPath(Entity.DateEnd) },
            ],
            [{ id: _getPath(Entity.IsCaringPersonally) }]
        ],
        lineItems: {
            collection: _getPath(Entity.CaregivingIntervals),
            order: PrSubmissionCaregivingIntervalEntity.Id,
            canReorder: false,
            isItemCloneable: false,
            isTheOnlyItemRemovable: false,
            canAdd: (args) => {
                if (!ifAny(isDurationType, isEndType)(args)) {
                    return false;
                }
                const { storage } = args;
                const isCaringPersonally = storage.getValue(storage.data.bindingContext.navigate(_getPath(Entity.IsCaringPersonally)));
                return !isCaringPersonally;
            },
            columns: [
                { id: PrSubmissionCaregivingIntervalEntity.DateFrom },
                { id: PrSubmissionCaregivingIntervalEntity.DateTo }
            ],
            render: (args) => {
                if (!ifAny(isDurationType, isEndType)(args)) {
                    return null;
                }
                return (<>
                    <LabelStyled>{i18next.t(`Submission:SocialEventsForm.${opts.labelPrefix}_CaregivingIntervals`)}</LabelStyled>
                    <SmartFastEntryList {...(args.props as unknown as ISmartFastEntryListProps)} />
                </>);
            }
        }
    }, PrSubmissionGroup.CareReceiver);
}
